import { IsLocalEnvironment } from '@shared/Helpers/AuthorizationUtils/IsLocalEnvironment/IsLocalEnvironment';
import { CashBuildElementIdEnum } from '@shared/Enums/CashBuildElementIdEnum';
import { IsValidEmail } from '@shared/Helpers/AuthorizationUtils/IsValidEmail/IsValidEmail';
import { useAuthorizationViewModel } from '@viewModels/AuthorizationViewModel/AuthorizationViewModel';
import { GetHostEmail } from '@shared/Helpers/CBGetAttributes/GetHostEmail/GetHostEmail';
import { useEffect, useRef } from 'react';
import { CustomProjectInfoViewModel } from '@viewModels/CustomProjectInfoViewModel';
import { useTemplatesViewModel } from '@viewModels/TemplatesViewModel/TemplatesViewModel';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@configs/AppRoutes';
import { StepsViewModeEnum } from '@shared/Enums/StepsViewModeEnum';
import { notifyErrorCustom } from '@shared/Widgets/ToastNotifications/ToastNotifications';

export const useHostEmailWatcher = () => {
    const { removeToken, getToken, bearerToken, authState, setAuthState } =
        useAuthorizationViewModel();

    const {
        setShouldRedirectToNewProjectAfterAuthorization,
        shouldRedirectToNewProjectAfterAuthorization,
        currentTemplate,
    } = useTemplatesViewModel();

    const navigate = useNavigate();
    const projectViewModel = new CustomProjectInfoViewModel();
    const prevLoggedInRef = useRef<boolean>(false);
    const isProcessingRef = useRef<boolean>(false);

    useEffect(() => {
        if (IsLocalEnvironment()) {
            return;
        }

        const root = document.getElementById(
            CashBuildElementIdEnum.ROOT_CASHBUILD
        );

        if (!root) {
            return;
        }

        if (authState) {
            prevLoggedInRef.current = authState;
        } else {
            const initialEmail = GetHostEmail();
            prevLoggedInRef.current = IsValidEmail(initialEmail);
        }

        const onEmailChange = async () => {
            const currentEmail = GetHostEmail();
            const currentLoggedIn = IsValidEmail(currentEmail);
            if (prevLoggedInRef.current && !currentLoggedIn) {
                removeToken();
                await getToken();
                setAuthState(false);
                setShouldRedirectToNewProjectAfterAuthorization(false);
            } else if (!prevLoggedInRef.current && currentLoggedIn) {
                await getToken();
                setAuthState(true);
            }

            if (
                currentLoggedIn &&
                shouldRedirectToNewProjectAfterAuthorization &&
                currentTemplate &&
                !isProcessingRef.current
            ) {
                isProcessingRef.current = true;
                try {
                    const newProjectResponse =
                        await projectViewModel.createNewProjectWithFreeTemplate(
                            [currentTemplate?.id]
                        );
                    if (!newProjectResponse.error && newProjectResponse.data) {
                        const path = ROUTES.STEP1.getPath(
                            StepsViewModeEnum.PROJECT,
                            String(newProjectResponse.data.id)
                        );
                        navigate(path);
                    }
                } catch (error) {
                    navigate(ROUTES.BOM);
                    notifyErrorCustom('Failed to create new project');
                }
                setShouldRedirectToNewProjectAfterAuthorization(false);
                isProcessingRef.current = false;
            }

            prevLoggedInRef.current = currentLoggedIn;
            setAuthState(currentLoggedIn);
        };

        const obs = new MutationObserver(onEmailChange);
        obs.observe(root, {
            attributes: true,
            attributeFilter: [CashBuildElementIdEnum.EMAIL],
        });

        onEmailChange();

        return () => {
            obs.disconnect();
        };
    }, [removeToken, getToken, bearerToken]);
};
